import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Divider,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

import { API_URL } from '../constants/urls';

interface OrganizationEssence {
  id?: string;
  organization_id?: string;
  credo?: string;
  origin_story?: string;
  drivers?: string;
  core_identity?: string;
  mantras?: string;
  anti_vision?: string;
  vision?: string;
  mission?: string;
  raison_detre?: string;
  unifying_idea?: string;
  created_at?: string;
  updated_at?: string;
}

interface EssenceManagementProps {
  organizationId: string;
  orgRole: string;
  organization?: any;
}

const essenceFields = [
  {
    key: 'credo',
    label: 'Credo',
    placeholder: 'The core belief system that guides your organization',
    description:
      'Your fundamental beliefs about business, customers, and value creation. This is the philosophical foundation that everything else builds upon.',
    rows: 4,
  },
  {
    key: 'origin_story',
    label: 'Origin Story',
    placeholder: "Why did you come together to do what you're doing?",
    description:
      'The founding story - what brought the founders together, what problem they saw, and why they decided to solve it.',
    rows: 4,
  },
  {
    key: 'drivers',
    label: 'Drivers',
    placeholder:
      'What drives you? (spark of inspiration, beliefs, frustrations, ideals, etc.)',
    description:
      "The motivating forces behind your work - could be inspiration, extreme beliefs, frustrations with the status quo, or ideals you're pursuing.",
    rows: 4,
  },
  {
    key: 'core_identity',
    label: 'Core Identity',
    placeholder: "What's at your center - defining who you are",
    description:
      'The unchanging essence of who you are as an organization - your DNA that remains constant regardless of what you do.',
    rows: 3,
  },
  {
    key: 'mantras',
    label: 'Mantras',
    placeholder: 'What behaviors and principles guide you day to day',
    description:
      'The repeated phrases, principles, or behaviors that guide daily decisions and actions throughout your organization.',
    rows: 3,
  },
  {
    key: 'anti_vision',
    label: 'Anti-Vision',
    placeholder: 'What will you never be or never do?',
    description:
      'Explicitly state what you oppose, reject, or will never become. This creates clarity by defining boundaries.',
    rows: 3,
  },
  {
    key: 'vision',
    label: 'Vision',
    placeholder: 'How do you see the world in the future?',
    description:
      "Your picture of the future world where your organization has succeeded - what's different and better.",
    rows: 3,
  },
  {
    key: 'mission',
    label: 'Mission',
    placeholder: 'What gets you out of bed every day?',
    description:
      "Your organization's purpose and primary objective - the work you do every day to achieve your vision.",
    rows: 3,
  },
  {
    key: 'raison_detre',
    label: "Raison d'être",
    placeholder: 'Why do you exist?',
    description:
      "Your reason for being - the fundamental justification for your organization's existence in the world.",
    rows: 3,
  },
  {
    key: 'unifying_idea',
    label: 'Unifying Idea',
    placeholder: 'The central concept that ties everything together',
    description:
      'The single idea or concept that connects all other elements of your essence - your North Star.',
    rows: 2,
  },
];

const EssenceManagement: React.FC<EssenceManagementProps> = ({
  organizationId,
  orgRole,
  organization,
}) => {
  const [essence, setEssence] = useState<OrganizationEssence>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (organizationId) {
      fetchEssence();
    }
  }, [organizationId]);

  const fetchEssence = async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organizationId}/essence`,
      );
      if (response.data) {
        setEssence(response.data);
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        setError('Failed to load essence document.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setEssence((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!organizationId) {
      setError('Organization ID is missing.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await axios.put(
        `${API_URL}/organizations/${organizationId}/essence`,
        essence,
      );
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save essence document.');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    const orgName = organization?.name || 'Organization';
    const orgDescription = organization?.description || '';
    const geographicMarkets = organization?.geographic_markets;
    const researchContext = organization?.research_context_notes;
    const date = new Date().toLocaleDateString();

    // Build the document content
    let content = `# ${orgName} - Organization Essence\n\n`;
    content += `Generated: ${date}\n\n`;

    // Add organization details section
    if (orgDescription || geographicMarkets || researchContext) {
      content += `## Organization Overview\n\n`;

      if (orgDescription) {
        content += `**Description:** ${orgDescription}\n\n`;
      }

      if (geographicMarkets) {
        const markets = Array.isArray(geographicMarkets)
          ? geographicMarkets.join(', ')
          : geographicMarkets;
        content += `**Geographic Markets:** ${markets}\n\n`;
      }

      if (researchContext) {
        content += `**Research Context:** ${researchContext}\n\n`;
      }
    }

    content += `---\n\n`;
    content += `# Organization Essence\n\n`;

    // Add each essence field with content
    essenceFields.forEach((field) => {
      const value = (essence as any)[field.key];
      if (value) {
        content += `## ${field.label}\n\n`;
        content += `${value}\n\n`;
      }
    });

    // Create and download the file
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${orgName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-essence.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4 sm:p-6">
          <Typography>Loading essence document...</Typography>
        </CardContent>
      </Card>
    );
  }

  const isAdmin = orgRole === 'admin';

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <Typography variant="h6" className="mb-4 text-lg sm:text-xl">
          Organization Essence
        </Typography>
        <Divider className="mb-4 sm:mb-6" />

        {success && (
          <Alert
            severity="success"
            className="mb-4"
            onClose={() => setSuccess(false)}
          >
            Essence document saved successfully.
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            className="mb-4"
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <div className="space-y-4">
          {essenceFields.map((field) => (
            <div key={field.key}>
              <label
                htmlFor={field.key}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {field.label}
              </label>
              <p className="text-sm text-gray-500 mb-2">{field.description}</p>
              <textarea
                id={field.key}
                rows={field.rows}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder={field.placeholder}
                value={(essence as any)[field.key] || ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                disabled={!isAdmin}
              />
              {!isAdmin && field.key === essenceFields[0].key && (
                <p className="text-xs text-gray-500 mt-1">
                  {orgRole === 'editor'
                    ? 'Only administrators can edit the organization essence.'
                    : 'You have read-only access to the organization essence.'}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="pt-4 flex justify-end gap-2">
          {/* Export button - available to all users */}
          <Button
            variant="outlined"
            onClick={handleExport}
            size="medium"
            startIcon={<DownloadIcon />}
            className="w-full sm:w-auto"
          >
            Export Essence
          </Button>

          {/* Save button - only for admins */}
          {isAdmin && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              size="medium"
              className="w-full sm:w-auto"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EssenceManagement;
